import type { OrdenCompraDTO, OrdenCompraInput, OrdenCompraResumenDTO, SugeridoCompraDTO, CompraDTO, CompraEditarInput, CompraInput, CompraLineaDTO, CompraPagoInput, CompraResumenDTO, EmpresaAgenteDTO, IslrConceptoDTO, NumerarComprobantesInput, ProveedorDTO, ProveedorEditarInput, ProveedorInput } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, getToken, patch, post, put } from '@/lib/api.ts';
import { conClave, useEnvioUnico } from '@/lib/idempotencia.ts';

export interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  limite: number;
}
export interface FiltrosCompras {
  estado?: string | undefined;
  proveedor_id?: string | undefined;
  periodo?: string | undefined;
  q?: string | undefined;
  con_saldo?: 'true' | undefined;
  /** Solo facturas o solo notas de entrega del proveedor (sin factura). */
  tipo_documento?: 'factura' | 'nota_entrega' | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export type CalculoCompra = Pick<CompraDTO, 'bruto' | 'descuento' | 'base_gravada' | 'base_exenta' | 'iva' | 'total_factura' | 'gastos_anexos' | 'retencion_iva' | 'retencion_islr' | 'neto' | 'islr_grupos' | 'tasa_bcv' | 'total_factura_bs' | 'base_gravada_bs' | 'base_exenta_bs' | 'iva_bs' | 'retencion_iva_bs' | 'retencion_islr_bs' | 'costos'> & { lineas: Omit<CompraLineaDTO, 'id'>[] };

export const clavesCompras = {
  lista: (f: FiltrosCompras) => ['compras', 'lista', f] as const,
  detalle: (id: string) => ['compras', 'detalle', id] as const,
  proveedores: (f: { q?: string | undefined; activo?: string | undefined }) => ['compras', 'proveedores', f] as const,
  empresa: ['compras', 'empresa'] as const,
};

export const useCompras = (f: FiltrosCompras) => useQuery({ queryKey: clavesCompras.lista(f), queryFn: () => get<Pagina<CompraResumenDTO>>('/compras', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useCompra = (id: string | null) => useQuery({ queryKey: clavesCompras.detalle(id ?? ''), queryFn: () => get<CompraDTO>(`/compras/${id}`), enabled: !!id });
export const useProveedores = (f: { q?: string | undefined; activo?: string | undefined; limite?: number }) => useQuery({ queryKey: clavesCompras.proveedores(f), queryFn: () => get<Pagina<ProveedorDTO>>('/proveedores', { ...f, limite: f.limite ?? 50 }), placeholderData: (p) => p });
export const useEmpresaAgente = () => useQuery({ queryKey: clavesCompras.empresa, queryFn: () => get<EmpresaAgenteDTO>('/compras/empresa'), staleTime: 10 * 60_000 });
export const useConceptosIslr = () => useQuery({ queryKey: ['compras', 'islr-conceptos'], queryFn: () => get<IslrConceptoDTO[]>('/compras/islr-conceptos'), staleTime: 30 * 60_000 });
export const calcularCompra = (datos: CompraInput) => post<CalculoCompra>('/compras/calcular', datos);
export const registrarImpresionComprobante = (compraId: string, cid: string) => post<{ impresiones: number; copia: boolean; reimpresion: boolean }>(`/compras/${compraId}/comprobantes/${cid}/impresion`);

function useInvalidar() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['compras'] }), qc.invalidateQueries({ queryKey: ['productos'] }), qc.invalidateQueries({ queryKey: ['inventario'] }), qc.invalidateQueries({ queryKey: ['cierres'] })]);
}
export function useCrearProveedor() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (datos: ProveedorInput) => post<ProveedorDTO>('/proveedores', datos), onSuccess: () => void inv() });
}
export function useEditarProveedor() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: ProveedorEditarInput }) => patch<ProveedorDTO>(`/proveedores/${id}`, datos), onSuccess: () => void inv() });
}
export function useCrearCompra() {
  const inv = useInvalidar();
  const enviar = useEnvioUnico((datos: CompraInput, clave: string) => post<CompraDTO>('/compras', datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => void inv() });
}
export function useEditarCompra() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: CompraEditarInput }) => put<CompraDTO & { requiere_regenerar_txt: boolean; requiere_regenerar_xml: boolean }>(`/compras/${id}`, datos), onSuccess: () => void inv() });
}
export function useConfirmarCompra() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (id: string) => post<CompraDTO>(`/compras/${id}/confirmar`), onSuccess: () => void inv() });
}
/** Retención provisional → definitiva: asigna número y período a los comprobantes sin numerar. */
export function useNumerarComprobantes() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: NumerarComprobantesInput }) => post<CompraDTO>(`/compras/${id}/comprobantes/numerar`, datos), onSuccess: () => void inv() });
}
export function useAnularCompra() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, motivo }: { id: string; motivo: string }) => post<CompraDTO>(`/compras/${id}/anular`, { motivo }), onSuccess: () => void inv() });
}
export function usePagarCompra() {
  const inv = useInvalidar();
  // Reintentar tras un corte de red no registra el pago dos veces: va la misma clave
  const enviar = useEnvioUnico(({ id, datos }: { id: string; datos: CompraPagoInput }, clave: string) => post<CompraDTO>(`/compras/${id}/pagos`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => void inv() });
}

/** Descarga autenticada (TXT / XLSX / XML) disparando el diálogo de guardar del navegador. */
export async function descargarArchivo(ruta: string, nombre: string): Promise<{ lineas: string | null; total: string | null }> {
  const res = await fetch(`/api${ruta}`, { headers: { authorization: `Bearer ${getToken() ?? ''}` } });
  if (!res.ok) {
    // El servidor explica por qué (p. ej. falta el código de concepto de ISLR en una compra)
    const cuerpo = (await res.json().catch(() => null)) as { error?: { mensaje?: string } } | null;
    throw new Error(cuerpo?.error?.mensaje ?? `No se pudo descargar (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { lineas: res.headers.get('x-lineas'), total: res.headers.get('x-total-retenido-bs') };
}

// ─── Órdenes de compra (spec 21) ────────────────────────────────────────────
export interface FiltrosOrdenes {
  estado?: string | undefined;
  q?: string | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export const useOrdenesCompra = (f: FiltrosOrdenes) => useQuery({ queryKey: ['compras', 'ordenes', 'lista', f], queryFn: () => get<Pagina<OrdenCompraResumenDTO>>('/ordenes-compra', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useOrdenCompra = (id: string | null) => useQuery({ queryKey: ['compras', 'ordenes', 'detalle', id ?? ''], queryFn: () => get<OrdenCompraDTO>(`/ordenes-compra/${id}`), enabled: !!id });
/** Lo que está bajo el mínimo de la sucursal y de sus tiendas y depósitos, menos lo ya pedido en otras órdenes abiertas. */
export const useSugeridosCompra = (q: string, excluir: string | null, habilitado: boolean) =>
  useQuery({ queryKey: ['compras', 'ordenes', 'sugeridos', q, excluir], queryFn: () => get<SugeridoCompraDTO[]>('/ordenes-compra/sugeridos', { ...(q ? { q } : {}), ...(excluir ? { excluir_orden_id: excluir } : {}) }), enabled: habilitado, placeholderData: (p) => p });
export function useGuardarOrdenCompra() {
  const inv = useInvalidar();
  const crear = useEnvioUnico((datos: OrdenCompraInput, clave: string) => post<OrdenCompraDTO>('/ordenes-compra', datos, conClave(clave)));
  return useMutation({ mutationFn: ({ id, datos }: { id: string | null; datos: OrdenCompraInput }) => (id ? put<OrdenCompraDTO>(`/ordenes-compra/${id}`, datos) : crear(datos)), onSuccess: () => void inv() });
}
export function useAnularOrdenCompra() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, motivo }: { id: string; motivo: string }) => post<OrdenCompraDTO>(`/ordenes-compra/${id}/anular`, { motivo }), onSuccess: () => void inv() });
}
export const registrarImpresionOrden = (id: string) => post<{ impresiones: number }>(`/ordenes-compra/${id}/impresion`);
